import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/AppLayout"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { IconFilePlus, IconFileText, IconRestore } from "@tabler/icons-react"
import { DocumentTree } from "./components/DocumentTree"
import type { TreeNode } from "./types"

interface Props {
  [key: string]: unknown
  tree: TreeNode[]
  recents: TreeNode[]
  trash: TreeNode[]
  can_create: boolean
}

export default function DocumentsIndex() {
  const { tree, recents, trash, can_create } = usePage<Props>().props

  return (
    <>
      <Head title="Documents" />

      <div className="flex flex-1">
        <aside className="bg-sidebar hidden w-64 shrink-0 border-r p-3 lg:block">
          {can_create && (
            <Button variant="outline" size="sm" className="mb-3 w-full" onClick={() => router.post("/documents", {})}>
              <IconFilePlus className="size-4" />
              New page
            </Button>
          )}
          <DocumentTree nodes={tree} />
        </aside>

        <main className="min-w-0 flex-1 px-8 py-10">
          <div className="mx-auto max-w-3xl space-y-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h1 className="text-2xl font-semibold">Documents</h1>
                <p className="text-sm text-muted-foreground">
                  Company pages everyone can read, and private pages only the people you add can see.
                </p>
              </div>

              {can_create && (
                <Button onClick={() => router.post("/documents", {})}>
                  <IconFilePlus className="size-4" />
                  New page
                </Button>
              )}
            </div>

            {tree.length === 0 && trash.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center">
                  <IconFileText className="mx-auto mb-3 size-8 text-muted-foreground" />
                  <p className="font-medium">No pages yet</p>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Start a page for yourself, then share it when it is worth reading.
                  </p>
                  {can_create && (
                    <Button onClick={() => router.post("/documents", {})}>
                      <IconFilePlus className="size-4" />
                      Create your first page
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {recents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recently edited</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {recents.map((node) => (
                    <Link
                      key={node.id}
                      href={`/documents/${node.id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                    >
                      <IconFileText className="size-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{node.title}</span>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {trash.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Trash</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {trash.map((node) => (
                    <div key={node.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                      <IconFileText className="size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate">{node.title}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Restore ${node.title}`}
                        onClick={() => router.patch(`/documents/${node.id}/restore`)}
                      >
                        <IconRestore className="size-4" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </>
  )
}

DocumentsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
